import React from "react";
import { CheckCircle2, Circle, AlertOctagon } from "lucide-react";
import { formatDate, ModeIcon, StatusChip } from "@/components/ventrix/atoms";

const STATUS_ORDER = ["BOOKED", "PICKED_UP", "IN_TRANSIT", "AT_HUB", "CUSTOMS", "OUT_FOR_DELIVERY", "DELIVERED"];

function EventItem({ ev, currentStatus }) {
  const isException = ev.status_code === "EXCEPTION";
  const isDone = STATUS_ORDER.indexOf(ev.status_code) <= STATUS_ORDER.indexOf(currentStatus);
  return (
    <li className="ml-6 pb-6 last:pb-0" data-testid={`event-${ev.status_code}`}>
      <span
        className="absolute -left-[9px] mt-1 flex items-center justify-center w-4 h-4 rounded-full bg-white"
        style={{ boxShadow: `0 0 0 2px ${isException ? "#d41f26" : isDone ? "#166534" : "#A9C0E0"}` }}
      >
        {isException ? (
          <AlertOctagon size={10} color="#d41f26" strokeWidth={2.5} />
        ) : isDone ? (
          <CheckCircle2 size={12} color="#166534" strokeWidth={2.5} />
        ) : (
          <Circle size={8} color="#A9C0E0" />
        )}
      </span>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className={`text-sm font-bold ${isException ? "text-[#d41f26]" : "text-[#0E2F76]"}`}>
          {ev.status_code.replace(/_/g, " ")}
        </span>
        <span className="text-xs font-mono text-[#475569]">{formatDate(ev.happened_at)}</span>
      </div>
      <div className="text-sm text-[#0f172a] mt-1">{ev.description}</div>
      {ev.location && (
        <div className="text-xs text-[#475569] mt-1 uppercase tracking-wider font-semibold">{ev.location}</div>
      )}
    </li>
  );
}

export default function TrackingTimeline({ events = [], currentStatus, legs = [] }) {
  const sorted = [...events].sort((a, b) => (a.happened_at || "").localeCompare(b.happened_at || ""));
  const multi = Array.isArray(legs) && legs.length > 1;

  if (!multi) {
    return (
      <ol className="relative border-l border-[#A9C0E0]/70 ml-3">
        {sorted.map((ev, i) => <EventItem key={i} ev={ev} currentStatus={currentStatus} />)}
      </ol>
    );
  }

  const orderedLegs = [...legs].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  return (
    <div className="space-y-6" data-testid="timeline-grouped">
      {orderedLegs.map((leg) => {
        const legEvents = sorted.filter((e) => (e.leg_sequence || 1) === leg.sequence);
        return (
          <div key={leg.sequence} data-testid={`leg-group-${leg.sequence}`}>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3">
              <span className="text-[10px] font-black tracking-widest uppercase text-[#475569]">Leg {leg.sequence}</span>
              <ModeIcon mode={leg.mode} size={13} />
              <span className="text-sm font-semibold text-[#0E2F76]">{leg.origin}</span>
              <span className="text-[#A9C0E0]">→</span>
              <span className="text-sm font-semibold text-[#0E2F76]">{leg.destination}</span>
              {leg.carrier_name && (
                <span className="text-[10px] uppercase tracking-wider font-bold text-[#475569]">
                  · {leg.carrier_name}{leg.carrier_type === "partner" ? " (partner)" : ""}
                </span>
              )}
              {leg.status && <StatusChip status={leg.status} className="ml-1" />}
            </div>
            {legEvents.length > 0 ? (
              <ol className="relative border-l border-[#A9C0E0]/70 ml-3">
                {legEvents.map((ev, i) => <EventItem key={i} ev={ev} currentStatus={currentStatus} />)}
              </ol>
            ) : (
              <div className="ml-3 pl-6 text-xs text-[#475569]">No milestones yet for this leg.</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
